

import Vue from 'vue'
import Component from "vue-class-component";
import {Prop, Watch} from "vue-property-decorator";
import {IUserImported} from "@/store/modules/userDiffusionExpressModule";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import CsvDownloader from "@/components/shared/CsvDownloader.vue";
import {
  DIFFUSION_EXPRESS_OPERATION_COLORS,
  DIFFUSION_EXPRESS_OPERATIONS,
  diffusionExpressOperation,
  EDiffusionExpressOperation,
  isDiffusionExpressHeaderOk,
} from "@/helpers/diffusionExpressResults";
import {PAGINATION_FIRST_PAGE, PAGINATION_PER_PAGE_OPTIONS} from "@/constants/pagination";

// csv field that caused each validation error, to highlight it
const ERROR_FIELDS: {[code: number]: string} = {
  13: 'email',
  105: 'phone_number',
  201: 'email',
  202: 'first_name',
  203: 'last_name',
  204: 'phone_number',
  254: 'phone_number',
};

/**
 * Result of a diffusion express run: what was done with each csv line and each locker resident
 */
@Component({
  components: {CsvDownloader, LockerChip, PaginationBar},
})
export default class DiffusionExpressResults extends Vue {

  @Prop({default: () => []})
  protected results!: IUserImported[];
  @Prop({default: () => []})
  protected lockers!: ILocker[];

  protected readonly operationColors = DIFFUSION_EXPRESS_OPERATION_COLORS;
  protected readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;
  protected operationFilter: EDiffusionExpressOperation|null = null;
  protected currentPage: number = PAGINATION_FIRST_PAGE;
  protected perPage: number = PAGINATION_PER_PAGE_OPTIONS[PAGINATION_PER_PAGE_OPTIONS.length - 1];

  @Watch('results')
  protected onResultsChanged() {
    this.operationFilter = null;
    this.currentPage = PAGINATION_FIRST_PAGE;
  }

  @Watch('operationFilter')
  protected onFilterChanged() {
    this.currentPage = PAGINATION_FIRST_PAGE;
  }

  // header line is left out unless it failed; errors first, then the rest as they came
  protected get rows(): IUserImported[] {
    const rows = this.results.filter(each => !isDiffusionExpressHeaderOk(each));
    const isError = (each: IUserImported) => this.operationOf(each) === EDiffusionExpressOperation.ERROR;
    return rows.filter(isError).concat(rows.filter(each => !isError(each)));
  }

  protected get filteredRows(): IUserImported[] {
    return this.operationFilter
      ? this.rows.filter(each => this.operationOf(each) === this.operationFilter)
      : this.rows;
  }

  protected get operationCounts(): {[key: string]: number} {
    const counts: {[key: string]: number} = {};
    this.rows.forEach(each => {
      const operation = this.operationOf(each);
      counts[operation] = (counts[operation] || 0) + 1;
    });
    return counts;
  }

  protected get operationsWithRows(): EDiffusionExpressOperation[] {
    return DIFFUSION_EXPRESS_OPERATIONS.filter(each => this.operationCounts[each] > 0);
  }

  protected get errorBreakdown(): {code: number, count: number}[] {
    const counts: {[code: number]: number} = {};
    this.rows
      .filter(each => this.operationOf(each) === EDiffusionExpressOperation.ERROR)
      .forEach(each => counts[each.code_import] = (counts[each.code_import] || 0) + 1);
    return Object.keys(counts).map(code => ({code: +code, count: counts[+code]}));
  }

  protected get invitationsNotSentCnt(): number {
    return this.rows.filter(each => each.invitation_sent === false).length;
  }

  // only override locker users applies the csv name / surname to existing users (and flags it with name_updated)
  protected get isOverride(): boolean {
    return this.rows.some(each => typeof each.name_updated === 'boolean');
  }

  // the csv phone is never applied to users that already existed, and the name only when overriding
  protected get hasExistingUsers(): boolean {
    return [EDiffusionExpressOperation.MOVED, EDiffusionExpressOperation.UNCHANGED, EDiffusionExpressOperation.RESTORED]
      .some(each => this.operationCounts[each] > 0);
  }

  protected get lastPage(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('users.userImport.line_number'), align: 'start', sortable: false, value: 'line' },
      { text: this.$t('users.userImport.operation'), align: 'start', sortable: false, value: 'operation' },
      { text: this.$t('users.userList.email'), align: 'start', sortable: false, value: 'email' },
      { text: this.$t('users.userList.firstName'), align: 'start', sortable: false, value: 'first_name' },
      { text: this.$t('users.userList.lastName'), align: 'start', sortable: false, value: 'last_name' },
      { text: this.$t('users.userList.phone'), align: 'start', sortable: false, value: 'phone_number' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: false, value: 'locker_id' },
      { text: this.$t('users.userImport.detail'), align: 'start', sortable: false, value: 'detail' },
      { text: this.$t('users.userList.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('users.userImport.delivery_token'), align: 'start', sortable: false, value: 'deliveryToken' },
    ];
  }

  protected get csvHeaders(): string[] {
    return [
      this.$t('users.userImport.line_number') as string,
      this.$t('users.userImport.operation') as string,
      this.$t('users.userList.email') as string,
      this.$t('users.userList.firstName') as string,
      this.$t('users.userList.lastName') as string,
      this.$t('users.userList.phone') as string,
      this.$t('lockers.locker.title') as string,
      this.$t('users.userImport.detail') as string,
      this.$t('users.userList.id') as string,
      this.$t('users.userImport.delivery_token') as string,
    ];
  }

  protected csvRow(row: IUserImported): Array<string|number|null|undefined> {
    return [
      row.line,
      this.$t('users.userImport.operations.' + this.operationOf(row)) as string,
      row.email,
      row.first_name,
      row.last_name,
      row.phone_number,
      this.lockerName(row),
      this.detailText(row),
      row.id,
      row.deliveryToken,
    ];
  }

  protected fetchCsvRows(): Promise<IUserImported[]> {
    return Promise.resolve(this.filteredRows);
  }

  protected operationOf(row: IUserImported): EDiffusionExpressOperation {
    return diffusionExpressOperation(row);
  }

  protected lockerName(row: IUserImported): string {
    if (row.locker_name) {
      return row.locker_name;
    }
    const locker = this.lockers.find(each => each.id == row.locker_id);
    return locker ? locker.name : '';
  }

  protected detailText(row: IUserImported): string {
    const text = this.operationDetailText(row);
    return row.name_updated && row.detail !== 'name_updated'
      ? text + ' · ' + this.$t('users.userImport.details.alsoNameUpdated')
      : text;
  }

  private operationDetailText(row: IUserImported): string {
    const t = (key: string, values?: any) => this.$t('users.userImport.details.' + key, values) as string;

    switch (this.operationOf(row)) {
      case EDiffusionExpressOperation.INVITED:
        if (row.invitation_sent === false) {
          return t('invitationNotSent');
        }
        return t(row.invitation_sent ? 'invitationSent' : 'invited');
      case EDiffusionExpressOperation.CREATED:
        return t('created');
      case EDiffusionExpressOperation.MOVED:
        if (row.detail === 'reactivated' || row.detail === 'name_updated') {
          return t(row.detail);
        }
        if (row.previous_community_name || row.previous_locker_name) {
          return t('movedFrom', {
            community: row.previous_community_name || '-',
            locker: row.previous_locker_name || t('noLocker'),
          });
        }
        return t('assigned');
      case EDiffusionExpressOperation.RESTORED:
        return t('restored');
      case EDiffusionExpressOperation.UNCHANGED:
        return t(row.detail === 'same_locker' || row.detail === 'same_community' ? row.detail : 'unchanged');
      case EDiffusionExpressOperation.DEACTIVATED:
        return t('deactivated');
      case EDiffusionExpressOperation.NOT_DEACTIVATED:
        return row.active_orders_count
          ? t('activeOrdersCount', {count: row.active_orders_count})
          : t('activeOrders');
      default:
        return row.detail && this.$te('users.userImport.details.' + row.detail)
          ? t(row.detail)
          : this.$t('users.userImport.resultCode.' + row.code_import) as string;
    }
  }

  protected fieldClass(row: IUserImported, field: string): string {
    const isErrorField = ERROR_FIELDS[row.code_import] === field
      || (row.code_import === 100 && (row as any)[field] === '');
    return isErrorField ? 'red--text font-weight-bold' : '';
  }

  protected onPageChange(page: number) {
    this.currentPage = page;
  }

  protected onPerPageChange(perPage: number) {
    this.perPage = perPage;
    this.currentPage = PAGINATION_FIRST_PAGE;
  }
}

